import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, IconButton } from '@mui/material';
import TCPACheckbox from './TCPACheckbox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { validatePhone } from '../utils/validation';

interface OnboardingFormProps {
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    tcpaConsent: boolean;
  }) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
  dealerName: string;
  isLiveAgentView: boolean;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({ onSubmit, onBack, isLoading, dealerName, isLiveAgentView }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    tcpaConsent: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      // newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    const validation = validatePhone(formData.phone);
    if (!validation.valid) {
      newErrors.phone = validation.error!;     
    }

    // if (!formData.phone.trim()) {
    //   newErrors.phone = 'Mobile number is required';
    // }

    if (!formData.tcpaConsent) {
      newErrors.tcpaConsent = 'You must agree to receive messages';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      await onSubmit(formData);
    }
  };

  return (
    <Box sx={{
      p: 3, overflowY: 'auto', flex: 1, "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#f1f1f1",
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "linear-gradient(to bottom right, #E1F7F4, #6A8AF7)",
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "linear-gradient(to bottom right, #E1F7F4, #6A8AF7)",
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={onBack}
          disabled={isLoading}
          sx={{ mr: 1, p: 0.5, pl: 0 }}
          aria-label="Go back"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          {isLiveAgentView ? 'Let\'s Connect!' : 'Let\'s get started!'}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isLiveAgentView ? 'Tell us about yourself and a live agent will reach out momentarily.' : 'Tell us about yourself to begin chatting with SAGE.'}
      </Typography>

      <TextField
        fullWidth
        label="First name*"
        value={formData.firstName}
        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
        error={!!errors.firstName}
        helperText={errors.firstName}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Last name*"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        error={!!errors.lastName}
        helperText={errors.lastName}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        type="email"
        label="Email (optional)"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        error={!!errors.email}
        helperText={errors.email}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        type="tel"
        label="Mobile number*"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        error={!!errors.phone}
        helperText={errors.phone}
        disabled={isLoading}
        sx={{ mb: 3 }}
      />

      <TCPACheckbox
        checked={formData.tcpaConsent}
        onChange={(checked) => setFormData({ ...formData, tcpaConsent: checked })}
        dealerName={dealerName}
        error={!!errors.tcpaConsent}
        disabled={isLoading}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isLoading}
        sx={{
          mt: 3,
          py: 1.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
      </Button>
    </Box>
  );
};

export default OnboardingForm;
